//@flow
import React from 'react'
import AppBar from '@material-ui/core/AppBar'
import { makeStyles } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu'
import Avatar from '@material-ui/core/Avatar'
//import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import DashboardIcon from '@material-ui/icons/Dashboard'
import PersonIcon from '@material-ui/icons/Person'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'

import { Link, useHistory } from 'react-router-dom'
import { useStore } from '../../store/useStore'
import { ActionLogout } from '../../store/user'
import * as genericApi from '@lesofi/phoenix-js/lib/phoenixgoapi/generic'
import * as batchRunStore from '../../store/batchRun'
import * as batchResultsetStore from '../../store/batchResultset'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  appBar: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    height: 64,
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    padding: theme.spacing(0, 3),
    minHeight: 64,
    display: 'flex',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  logoIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
    fontSize: '1.75rem',
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    "&:hover": {
      textDecoration: 'none',  
    }
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 40,
    height: 40,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
    border: '2px solid #fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  },
  profileButton: {
    padding: 4,
    marginLeft: theme.spacing(2),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.5),
    minWidth: 200,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  },
  menuIcon: {
    marginRight: theme.spacing(2),
    fontSize: '1.2rem',
    color: theme.palette.text.secondary,
  },
  menuText: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  }
}));

function AppHeader(): React$Node {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const history = useHistory();
  
  const { state, dispatch } = useStore();
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const clearLocalStorage = () => {
    localStorage.removeItem(batchRunStore.getStateName());
    localStorage.removeItem(batchResultsetStore.getStateName());
  };

  const handleLogout = () => {
    handleClose();
    
    // First dispatch logout action to clear state immediately
    dispatch({ type: ActionLogout });
    
    // Clear additional localStorage items
    clearLocalStorage();
    
    // Call API to logout server-side
    genericApi.appUserLogoutApi().then(() => {
      // Use React Router for navigation to avoid cache issues
      history.replace('/login');
    }).catch(() => {
      // Even if API fails, still navigate to login since we cleared state
      history.replace('/login');
    });
  };
  
  const firstName = state.user.userInfo && state.user.userInfo.firstName;
  const lastName = state.user.userInfo && state.user.userInfo.lastName;
  //const email = state.user.userInfo && state.user.userInfo.email;
  // Fallback to providing initials even if first/last name aren't available
  const initials = firstName && lastName ? 
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() : 
    (state.user.userInfo && state.user.userInfo.email ? 
      state.user.userInfo.email.charAt(0).toUpperCase() : 
      'U');
  
  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar variant="regular" className={classes.toolbar}>
          <Link to="/" className={classes.title}>
            <div className={classes.logo}>
              <DashboardIcon className={classes.logoIcon} />
              <Typography variant="h6" noWrap>
                Admin Console
              </Typography>
            </div>
          </Link>
          
          {state.user.loggedIn && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {(firstName || lastName) && (
                <Typography variant="body1" style={{ marginRight: '10px', fontWeight: 500 }}>
                  {firstName} {lastName}
                </Typography>
              )}
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                className={classes.profileButton}
              >
                <Avatar className={classes.avatar}>
                  {initials}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  elevation: 4,
                  style: {
                    marginTop: 8,
                    minWidth: 190,
                    zIndex: 9999
                  }
                }}
              >
                <MenuItem 
                  className={classes.menuItem} 
                  onClick={handleClose} 
                  component={Link} 
                  to="/profile"
                >
                  <PersonIcon className={classes.menuIcon} />
                  <Typography className={classes.menuText}>Profile</Typography>
                </MenuItem>
                <Divider />
                <MenuItem 
                  className={classes.menuItem} 
                  onClick={handleLogout}
                >
                  <ExitToAppIcon className={classes.menuIcon} />
                  <Typography className={classes.menuText}>Logout</Typography>
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
    </div>
  );
}

export default (React.memo<{||}>(AppHeader): React$AbstractComponent<{||}, mixed>);